import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Stack,
  Paper,
  Typography,
  Breadcrumbs,
  TextField,
  MenuItem,
  Chip,
  Pagination,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Collapse,
  Button,
  Badge,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterListOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  fetchAuditLogs,
  fetchAuditFacets,
  setQuery,
} from "./auditLogSlice";
import AuditLogDetailDrawer from "./AuditLogDetailDrawer";

const SEVERITY_COLOR = {
  info: { bg: "#ccfbf1", fg: "#0f766e" },
  warning: { bg: "#fef3c7", fg: "#b45309" },
  critical: { bg: "#fee2e2", fg: "#b91c1c" },
};

const labelize = (s) =>
  String(s || "")
    .split("_")
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");

const SeverityChip = ({ severity }) => {
  const c = SEVERITY_COLOR[severity] || SEVERITY_COLOR.info;
  return (
    <Chip
      label={severity || "info"}
      size="small"
      sx={{
        height: 18,
        fontSize: "0.625rem",
        fontWeight: 600,
        backgroundColor: c.bg,
        color: c.fg,
      }}
    />
  );
};

const ActionChip = ({ action }) => (
  <Chip
    label={labelize(action)}
    size="small"
    sx={{
      height: 18,
      fontSize: "0.625rem",
      fontWeight: 600,
      backgroundColor: "primary.light",
      color: "primary.main",
    }}
  />
);

const fieldSx = { "& .MuiInputBase-input": { fontSize: "0.8125rem" } };

const QUERY_KEYS = [
  "page",
  "limit",
  "search",
  "customer",
  "user",
  "imei",
  "actionType",
  "entityType",
  "severity",
  "outcome",
  "startDate",
  "endDate",
];

const readQueryFromParams = (params) => {
  const obj = {};
  QUERY_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v == null || v === "") return;
    if (k === "page" || k === "limit") obj[k] = Number(v);
    else obj[k] = v;
  });
  return obj;
};

const buildSearchParams = (q) => {
  const out = {};
  QUERY_KEYS.forEach((k) => {
    const v = q[k];
    if (v == null) return;
    if (typeof v === "string" && !v) return;
    if ((k === "page" && v === 1) || (k === "limit" && v === 25)) return;
    out[k] = String(v);
  });
  return out;
};

export default function AuditLogPage() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams, setSearchParams] = useSearchParams();
  const { items, pagination, query, loading, facets } = useSelector(
    (s) => s.auditLog
  );

  const [searchInput, setSearchInput] = useState("");
  const [moreOpen, setMoreOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const debounceRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const fromUrl = readQueryFromParams(searchParams);
    dispatch(setQuery({ ...fromUrl, page: fromUrl.page || 1 }));
    setSearchInput(fromUrl.search || "");
    dispatch(fetchAuditFacets());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(fetchAuditLogs(query));
    setSearchParams(buildSearchParams(query), { replace: true });
    // eslint-disable-next-line
  }, [query, dispatch]);

  // Infinite scroll on mobile — fetches the next page and appends instead of
  // replacing. Mirrors the Companies-list pattern. Desktop keeps top-pagination.
  const loadMoreRef = useRef();
  loadMoreRef.current = async () => {
    if (loadingMore || loading) return;
    if (pagination.page >= pagination.pages) return;
    setLoadingMore(true);
    const params = {
      ...query,
      page: pagination.page + 1,
      append: true,
    };
    try {
      await dispatch(fetchAuditLogs(params));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!isMobile) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreRef.current?.();
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile, items.length, pagination.page, pagination.pages]);

  const update = (patch) => dispatch(setQuery({ ...patch, page: 1 }));

  const onSearchInput = (v) => {
    setSearchInput(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => update({ search: v.trim() }), 800);
  };

  const selectedId = searchParams.get("selected");
  const openLog = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("selected", id);
    setSearchParams(next, { replace: true });
  };
  const closeLog = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("selected");
    setSearchParams(next, { replace: true });
  };

  const extraCount = [
    query.customer,
    query.user,
    query.imei,
    query.entityType,
    query.severity,
    query.outcome,
    query.startDate,
    query.endDate,
  ].filter(Boolean).length;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: { md: "100%" },
        minHeight: 0,
      }}
    >
      <Box mb={1.5} sx={{ flexShrink: 0 }}>
        <Breadcrumbs
          separator="›"
          sx={{ fontSize: "0.9375rem", display: { xs: "none", sm: "flex" } }}
        >
          <Typography color="text.secondary" sx={{ fontSize: "0.9375rem" }}>Home</Typography>
          <Typography color="text.primary" sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
            Audit Log
          </Typography>
        </Breadcrumbs>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          overflow: "hidden",
          flex: { md: 1 },
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: "wrap", rowGap: 1, alignItems: "center" }}
            useFlexGap
          >
            <TextField
              size="small"
              placeholder="Search summary, IMEI, user, action…"
              value={searchInput}
              onChange={(e) => onSearchInput(e.target.value)}
              sx={{ ...fieldSx, flex: "1 1 220px", minWidth: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              select
              label="Action"
              value={query.actionType || ""}
              onChange={(e) => update({ actionType: e.target.value })}
              sx={{ ...fieldSx, flex: "0 1 160px", minWidth: 130 }}
            >
              <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>All</MenuItem>
              {facets.actionTypes.map((a) => (
                <MenuItem key={a} value={a} sx={{ fontSize: "0.8125rem" }}>
                  {labelize(a)}
                </MenuItem>
              ))}
            </TextField>
            {isMobile && (
              <Button
                size="small"
                variant={moreOpen ? "contained" : "outlined"}
                onClick={() => setMoreOpen((v) => !v)}
                startIcon={
                  <Badge
                    badgeContent={extraCount}
                    color="error"
                    invisible={!extraCount}
                    sx={{ "& .MuiBadge-badge": { fontSize: "0.5625rem", height: 14, minWidth: 14 } }}
                  >
                    <FilterListIcon fontSize="small" />
                  </Badge>
                }
                sx={{ flexShrink: 0 }}
              >
                Filters
              </Button>
            )}
          </Stack>

          <Collapse in={!isMobile || moreOpen} timeout={200}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                flexWrap: "wrap",
                rowGap: 1,
                alignItems: "center",
                mt: 1,
                pt: isMobile ? 1 : 0,
                borderTop: isMobile ? "1px solid" : 0,
                borderColor: "divider",
              }}
              useFlexGap
            >
              <TextField
                size="small"
                select
                label="Entity"
                value={query.entityType || ""}
                onChange={(e) => update({ entityType: e.target.value })}
                sx={{ ...fieldSx, flex: "0 1 150px", minWidth: 130 }}
              >
                <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>All</MenuItem>
                {facets.entityTypes.map((e) => (
                  <MenuItem key={e} value={e} sx={{ fontSize: "0.8125rem" }}>
                    {labelize(e)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Severity"
                value={query.severity || ""}
                onChange={(e) => update({ severity: e.target.value })}
                sx={{ ...fieldSx, flex: "0 1 130px", minWidth: 110 }}
              >
                <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>All</MenuItem>
                {facets.severities.map((s) => (
                  <MenuItem key={s} value={s} sx={{ fontSize: "0.8125rem" }}>
                    {labelize(s)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Outcome"
                value={query.outcome || ""}
                onChange={(e) => update({ outcome: e.target.value })}
                sx={{ ...fieldSx, flex: "0 1 130px", minWidth: 110 }}
              >
                <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>All</MenuItem>
                {facets.outcomes.map((o) => (
                  <MenuItem key={o} value={o} sx={{ fontSize: "0.8125rem" }}>
                    {labelize(o)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Customer"
                value={query.customer || ""}
                onChange={(e) => update({ customer: e.target.value })}
                sx={{ ...fieldSx, flex: "1 1 140px", minWidth: 0 }}
              />
              <TextField
                size="small"
                label="User"
                value={query.user || ""}
                onChange={(e) => update({ user: e.target.value })}
                sx={{ ...fieldSx, flex: "1 1 140px", minWidth: 0 }}
              />
              <TextField
                size="small"
                label="IMEI"
                value={query.imei || ""}
                onChange={(e) => update({ imei: e.target.value })}
                sx={{ ...fieldSx, flex: "1 1 140px", minWidth: 0 }}
              />
              <TextField
                size="small"
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={query.startDate || ""}
                onChange={(e) => update({ startDate: e.target.value })}
                sx={{ ...fieldSx, flex: "1 1 130px", minWidth: 0 }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={query.endDate || ""}
                onChange={(e) => update({ endDate: e.target.value })}
                sx={{ ...fieldSx, flex: "1 1 130px", minWidth: 0 }}
              />
            </Stack>
          </Collapse>
        </Box>

        {/* Pagination — desktop / tablet only, sits ABOVE the table like the
            Companies list. Mobile uses infinite scroll instead. */}
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            p: 1,
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            display: { xs: "none", md: "flex" },
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Showing {items.length} of {pagination.total} · Page{" "}
            {pagination.page} of {Math.max(pagination.pages, 1)}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Pagination
              size="small"
              page={pagination.page}
              count={Math.max(pagination.pages, 1)}
              onChange={(_, p) => dispatch(setQuery({ page: p }))}
              shape="rounded"
              siblingCount={1}
              boundaryCount={1}
            />
          </Box>
        </Stack>

        {isMobile ? (
          <Box sx={{ p: 1, backgroundColor: "background.default", flex: 1, overflow: "auto", minHeight: 0 }}>
            {loading && items.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {!loading && items.length === 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "center", py: 4 }}
              >
                No audit entries match these filters.
              </Typography>
            )}
            <Stack spacing={0.75}>
              {items.map((l) => {
                const labelSx = {
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  fontWeight: 600,
                  minWidth: 70,
                  flexShrink: 0,
                };
                const valueSx = {
                  fontSize: "0.875rem",
                  color: "text.primary",
                  fontWeight: 500,
                  wordBreak: "break-word",
                };
                const companyDevice = [
                  l.company?.name,
                  l.device?.imei,
                ]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <Paper
                    key={l._id}
                    variant="outlined"
                    onClick={() => openLog(l._id)}
                    sx={{
                      p: 1.25,
                      cursor: "pointer",
                      backgroundColor: "primary.main",
                      borderColor: "primary.dark",
                      transition: "background-color .15s, border-color .15s",
                      "& .MuiTypography-root": { color: "#ffffff" },
                      "& .MuiSvgIcon-root": { color: "#ccfbf1" },
                      "&:hover": {
                        backgroundColor: "primary.dark",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      mb={0.75}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <ActionChip action={l.actionType} />
                      <SeverityChip severity={l.severity} />
                      <Box sx={{ flex: 1 }} />
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ fontSize: "0.7rem" }}
                      >
                        {dayjs(l.createdAt).format("DD MMM HH:mm")}
                      </Typography>
                    </Stack>
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1}>
                        <Typography sx={labelSx}>Summary:</Typography>
                        <Typography sx={valueSx}>{l.summary || "—"}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Typography sx={labelSx}>Actor:</Typography>
                        <Typography sx={valueSx}>
                          {l.actor?.name || l.actor?.username || "—"}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Typography sx={labelSx}>Company:</Typography>
                        <Typography sx={valueSx}>
                          {companyDevice || "—"}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>

            {/* Infinite-scroll sentinel + status text (mobile only) */}
            {items.length > 0 && (
              <Box
                ref={sentinelRef}
                sx={{ py: 2, textAlign: "center", color: "text.secondary" }}
              >
                {loadingMore ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <CircularProgress size={16} />
                    <Typography variant="caption">Loading more…</Typography>
                  </Stack>
                ) : pagination.page < pagination.pages ? (
                  <Typography variant="caption">Scroll for more</Typography>
                ) : (
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    All {pagination.total} entries loaded
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 130, whiteSpace: "nowrap" }}>When</TableCell>
                  <TableCell sx={{ width: 110, whiteSpace: "nowrap" }}>Action</TableCell>
                  <TableCell sx={{ width: 100, whiteSpace: "nowrap" }}>Entity</TableCell>
                  <TableCell sx={{ width: 90, whiteSpace: "nowrap" }}>Severity</TableCell>
                  <TableCell>Summary</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>Actor</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>Company / Device</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={20} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No audit entries match these filters.
                    </TableCell>
                  </TableRow>
                )}
                {items.map((l) => (
                  <TableRow
                    key={l._id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => openLog(l._id)}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.7rem", color: "text.secondary" }}>
                      {dayjs(l.createdAt).format("DD MMM HH:mm:ss")}
                    </TableCell>
                    <TableCell><ActionChip action={l.actionType} /></TableCell>
                    <TableCell sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                      {labelize(l.entityType)}
                    </TableCell>
                    <TableCell><SeverityChip severity={l.severity} /></TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {l.summary}
                      {l.changes?.length > 0 && (
                        <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                          · {l.changes.length} change{l.changes.length === 1 ? "" : "s"}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                      {l.actor?.name || l.actor?.username || "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {l.company?.name ? (
                        <Stack spacing={0.1}>
                          <Typography
                            component="div"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              color: "text.primary",
                              wordBreak: "break-word",
                            }}
                          >
                            {l.company.manufacturing_id != null
                              ? `#${l.company.manufacturing_id} · ${l.company.name}`
                              : l.company.name}
                          </Typography>
                          {l.device?.imei && (
                            <Typography
                              component="div"
                              variant="caption"
                              sx={{
                                fontFamily: "monospace",
                                color: "text.secondary",
                                fontSize: "0.6875rem",
                              }}
                            >
                              {l.device.imei}
                            </Typography>
                          )}
                        </Stack>
                      ) : l.device?.imei ? (
                        <Typography
                          component="div"
                          sx={{
                            fontFamily: "monospace",
                            color: "text.secondary",
                            fontSize: "0.6875rem",
                          }}
                        >
                          {l.device.imei}
                        </Typography>
                      ) : (
                        <Typography
                          component="span"
                          sx={{ color: "text.disabled" }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      </Paper>

      <AuditLogDetailDrawer logId={selectedId} onClose={closeLog} />
    </Box>
  );
}
